import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { postsListQueryOptions } from '@/lib/posts';

const PREVIEW_LIMIT = 5;

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาทีที่แล้ว`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} ชม. ที่แล้ว`;
  return new Date(iso).toLocaleDateString('th-TH', { month: 'short', day: 'numeric' });
}

/**
 * Editorial queue for users with `post.publish`. Shows the top 5
 * posts in status=pending_review with author + submission time, and
 * a "ดูทั้งหมด" link that pre-filters /posts to the same status.
 *
 * No inline approve action — publishing a post is a deliberate gesture
 * that benefits from the editor's full preview surface (cover image,
 * categories, SEO panel). One-click "publish from dashboard" risks
 * shipping content the publisher hasn't read.
 *
 * Silent-by-default — renders null when the queue is empty so a
 * normal day stays uncluttered.
 */
export function PendingReviewCard() {
  const query = useQuery(
    postsListQueryOptions({
      status: ['pending_review'],
      perPage: PREVIEW_LIMIT,
      sortBy: 'updatedAt',
      sortDir: 'desc',
    }),
  );
  const items = query.data?.items ?? [];
  const total = query.data?.pagination.total ?? 0;

  if (!query.isLoading && items.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Inbox className="h-4 w-4 text-amber-500" />
          รอตรวจก่อนเผยแพร่
          {total > 0 && (
            <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">
              {total}
            </span>
          )}
        </CardTitle>
        <Link
          to="/posts"
          search={{ status: 'pending_review' }}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          ดูทั้งหมด →
        </Link>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <p className="text-sm text-slate-500">กำลังโหลด...</p>
        ) : (
          <ul className="space-y-2 text-xs">
            {items.map((p) => (
              <li
                key={p.id}
                className="flex items-baseline justify-between gap-2 border-b border-slate-100 pb-1.5 last:border-0 dark:border-slate-800"
              >
                <Link
                  to="/posts/$postId"
                  params={{ postId: p.id }}
                  className="hover:text-brand-600 dark:hover:text-brand-400 min-w-0 flex-1"
                >
                  <div className="truncate text-sm font-medium text-slate-700 dark:text-slate-200">
                    {p.title}
                  </div>
                  <div className="truncate text-[11px] text-slate-500">{p.authorName}</div>
                </Link>
                <span className="shrink-0 text-[11px] text-slate-400">
                  {formatRelative(p.updatedAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
